import { ApiClientError } from '@igoal/api-client';
import { changePasswordSchema } from '@igoal/shared';
import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { Button, ErrorBanner, Field, Input, PageHeader, Panel, useToast } from '../components/ui';
import { api, errorMessage, fieldErrors } from '../lib/api';
import { useAuth } from '../lib/auth';

export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const me = await login(email, password);
      navigate(me.mustChangePassword ? '/account/password' : '/', { replace: true });
    } catch (err) {
      if (err instanceof ApiClientError && err.code === 'ACCOUNT_LOCKED') {
        const until = (err.details as { lockedUntil?: string })?.lockedUntil;
        setError(`Too many failed attempts. Try again ${until ? `after ${new Date(until).toLocaleTimeString()}` : 'later'}, or ask your administrator to reset your password.`);
      } else setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="grid min-h-dvh lg:grid-cols-[1fr_minmax(420px,40%)]">
      <div className="relative hidden overflow-hidden bg-sidebar lg:block" aria-hidden>
        {/* stock grid motif: one column per location */}
        <div className="absolute inset-0 grid grid-cols-6 gap-px p-px opacity-90">
          {Array.from({ length: 6 * 14 }).map((_, i) => {
            const col = i % 6;
            const v = (i * 37 + col * 11) % 23;
            return (
              <div
                key={i}
                className="flex items-end justify-end p-3 text-sm num"
                style={{
                  background: col === 0 ? '#22404a' : '#212e3b',
                  color: v === 0 ? '#b3261e' : v < 5 ? '#d9a04a' : 'rgba(255,255,255,0.28)',
                }}
              >
                {v === 0 ? '0' : v * (col === 0 ? 9 : 1)}
              </div>
            );
          })}
        </div>
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-sidebar via-sidebar/90 to-transparent px-12 pt-32 pb-12">
          <p className="max-w-md text-xl leading-snug font-medium text-white">Stock and prices for the central warehouse and every branch, in one place.</p>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-12">
        <form onSubmit={submit} className="w-full max-w-sm">
          <h1 className="mb-1 text-xl font-semibold">Sign in to I-GOAL</h1>
          <p className="mb-6 text-sm text-ink-soft">Use the email and password your administrator gave you.</p>
          <ErrorBanner>{error}</ErrorBanner>
          <Field label="Email" className="mb-3">
            <Input type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} autoFocus />
          </Field>
          <Field label="Password" className="mb-5">
            <Input type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
          </Field>
          <Button type="submit" variant="primary" busy={busy} className="w-full">
            Sign in
          </Button>
        </form>
      </div>
    </div>
  );
}

export function ChangePasswordPage() {
  const { me, refresh } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const [form, setForm] = useState({ currentPassword: '', newPassword: '', confirm: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const forced = me?.mustChangePassword;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError('');
    const parsed = changePasswordSchema.safeParse(form);
    const errs: Record<string, string> = {};
    if (!parsed.success) for (const i of parsed.error.issues) errs[i.path.join('.')] ??= i.message;
    if (form.newPassword !== form.confirm) errs.confirm = 'Passwords do not match';
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setBusy(true);
    try {
      await api.auth.changePassword({ currentPassword: form.currentPassword, newPassword: form.newPassword });
      await refresh();
      toast('Password changed. Other devices have been signed out.');
      navigate('/', { replace: true });
    } catch (err) {
      setErrors(fieldErrors(err));
      if (!Object.keys(fieldErrors(err)).length) setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  const body = (
    <form onSubmit={submit} className="max-w-sm">
      <ErrorBanner>{error}</ErrorBanner>
      <Field label="Current password" error={errors.currentPassword} className="mb-3">
        <Input type="password" autoComplete="current-password" value={form.currentPassword} onChange={(e) => setForm({ ...form, currentPassword: e.target.value })} />
      </Field>
      <Field label="New password" error={errors.newPassword} hint="At least 8 characters, with a letter and a number" className="mb-3">
        <Input type="password" autoComplete="new-password" value={form.newPassword} onChange={(e) => setForm({ ...form, newPassword: e.target.value })} />
      </Field>
      <Field label="Confirm new password" error={errors.confirm} className="mb-5">
        <Input type="password" autoComplete="new-password" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} />
      </Field>
      <Button type="submit" variant="primary" busy={busy}>
        Change password
      </Button>
    </form>
  );

  if (forced) {
    return (
      <div className="flex min-h-dvh items-center justify-center px-6">
        <div className="w-full max-w-sm">
          <h1 className="mb-1 text-xl font-semibold">Choose a new password</h1>
          <p className="mb-6 text-sm text-ink-soft">You signed in with a temporary password. Set your own to continue.</p>
          {body}
        </div>
      </div>
    );
  }
  return (
    <>
      <PageHeader title="Change password" description="Changing your password signs you out on every other device." />
      <Panel className="p-5">{body}</Panel>
    </>
  );
}
